import { useCallback, useEffect, useRef, useState } from 'react';
import {
  requestOrderPdf,
  retryOrderPdf,
  openPdfPlaceholderWindow,
  ORDER_PDF_STATUS
} from '../utils/orderPdf';

// T088 / ARCH-330 — thin React binding over the shared orderPdf client. Owns NO polling logic (that
// lives in orderPdf.js); it only tracks the current status/url for one component and guarantees the
// in-flight request is detached on unmount so no state update or poll fires after teardown.
//
// Usage:
//   const pdf = useOrderPdf();
//   <Icon onClick={() => pdf.openPdf({ orderId, route, locale, token })} />
//   {pdf.status === 'PREPARING' && <Spinner/>}   // or embed pdf.url when openPdf:false
export default function useOrderPdf() {
  const [status, setStatus] = useState(ORDER_PDF_STATUS.IDLE);
  const [url, setUrl] = useState(null);
  const [stage, setStage] = useState(null); // backend 202 preparation stage (e.g. ENRICHMENT), when present
  const [retryable, setRetryable] = useState(false);
  const mountedRef = useRef(true);
  const abortRef = useRef(null);
  const lastRequestRef = useRef(null); // { orderId, route, token } — needed so retry() targets the SAME identity

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  // route: full URL or apiHost-relative path (may carry ?lang). token: signing JWT for the customer
  // signing page; omitted for internal (Bearer) callers. openPdf:false → caller embeds pdf.url itself.
  const openPdf = useCallback(
    ({ orderId, route, locale, token, openPdf: shouldOpen = true } = {}) => {
      const previousController = abortRef.current;
      const controller =
        typeof AbortController !== 'undefined' ? new AbortController() : null;
      abortRef.current = controller;

      let resolvedRoute = route;
      if (
        locale &&
        route &&
        route.indexOf('lang=') === -1 &&
        route.indexOf('language=') === -1
      ) {
        resolvedRoute = `${route}${
          route.indexOf('?') === -1 ? '?' : '&'
        }lang=${locale}`;
      }
      lastRequestRef.current = { orderId, route: resolvedRoute, token };

      setUrl(null);
      setStage(null);
      setRetryable(false);
      setStatus(ORDER_PDF_STATUS.PREPARING);

      // Must happen synchronously here (inside the click) or the browser blocks the tab after polling.
      const popup = shouldOpen ? openPdfPlaceholderWindow() : null;
      const result = requestOrderPdf({
        orderId,
        route: resolvedRoute,
        token,
        openPdf: shouldOpen,
        popup,
        signal: controller ? controller.signal : undefined,
        onStatus: next => {
          if (!mountedRef.current || !next) return;
          setStatus(next.status);
          setStage(next.stage || null);
          setRetryable(Boolean(next.retryable));
          if (next.url) setUrl(next.url);
        }
      }).then(final => {
        if (mountedRef.current && final && !final.cancelled) {
          setStatus(final.status);
          setStage(final.stage || null);
          setRetryable(Boolean(final.retryable));
          if (final.url) setUrl(final.url);
        }
        return final;
      });

      // Detach the PREVIOUS subscription only AFTER the new one is already registered above — never
      // abort-then-resubscribe. Aborting first would unsubscribe (and, if it was the run's only
      // subscriber, cancel()/delete) a kept-alive FAILED+retryable run before requestOrderPdf() even
      // gets a chance to see it and issue a job-scoped retry (e.g. a "Download" click reusing the
      // SAME orderId/route as the automatic mount request).
      if (previousController) previousController.abort();

      return result;
    },
    []
  );

  // Retries the CURRENT active job for the last-requested identity (never re-derives from order
  // content — orderPdf.js's run tracks the active jobId itself). No-op if nothing is retryable.
  const retry = useCallback(() => {
    if (!lastRequestRef.current) return false;
    return retryOrderPdf(lastRequestRef.current);
  }, []);

  return {
    status,
    url,
    stage,
    retryable,
    openPdf,
    retry,
    cancel,
    STATUS: ORDER_PDF_STATUS
  };
}
